import * as React from 'react';
import * as ScriptLoader from './loader';
import Utils, { getTinymce } from './utils';

export interface EditorProps {
    id: string;
    inline: boolean;
    initialValue: string;
    onEditorChange: Function;
    onFloating: Function;
    value: string;
    init: Record<string, any>;
    tagName: string;
    plugins: string | string[];
    toolbar: string | string[];
    imgMaxWidth?: number;
}

const scriptState = ScriptLoader.create();

export default class Editor extends React.Component<Partial<EditorProps>> {
    private element: Element | null = null;
    private id?: string;
    private editor?: Record<any, any>;
    private inline?: boolean;
    private currentContent?: string | null;

    UNSAFE_componentWillMount() {
        this.id = this.id || this.props.id || Utils.uuid('fzzf-react');
        this.inline = this.props.inline ? this.props.inline : this.props.init && this.props.init.inline;
    }

    componentDidMount() {
        if (getTinymce() !== null) {
            this.initialise();
        } else if (this.element) {
            const doc = this.element.ownerDocument;
            ScriptLoader.load(
                scriptState,
                doc,
                window.config.other_file('/vender/tinymce/tinymce.min.js'),
                this.initialise,
            );
        }
    }

    componentWillUnmount() {
        if (getTinymce() !== null) {
            getTinymce().remove(this.editor);
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps: Partial<EditorProps>) {
        if (this.editor && this.editor.initialized) {
            this.currentContent = this.currentContent || this.editor.getContent();
            if (
                typeof nextProps.value === 'string' &&
                nextProps.value !== this.props.value &&
                nextProps.value !== this.currentContent
            ) {
                this.editor.setContent(nextProps.value);
            }
        }
    }

    render() {
        return this.inline ? this.renderInline() : this.renderIframe();
    }

    private initialise = () => {
        const finalInit = {
            ...this.props.init,
            target: this.element,
            inline: this.inline,
            plugins: Utils.mergePlugins(this.props.init && this.props.init.plugins, this.props.plugins),
            toolbar: this.props.toolbar || (this.props.init && this.props.init.toolbar),
            setup: (editor: any) => {
                this.editor = editor;
                editor.on('init', (e: Event) => {
                    this.initEditor(e, editor);
                });
                if (this.props.init && typeof this.props.init.setup === 'function') {
                    this.props.init.setup(editor);
                }
            },
        };
        // 清除默认样式
        if (Utils.isTextarea(this.element)) {
            this.element.style.visibility = '';
        }
        getTinymce().init(finalInit);
    };

    /**
     * 初始化富文本
     * @param {Event} initEvent
     * @param editor
     */
    private initEditor(initEvent: Event, editor: any) {
        const value =
            typeof this.props.value === 'string'
                ? this.props.value
                : typeof this.props.initialValue === 'string'
                    ? this.props.initialValue
                    : '';
        // 初始化内容
        editor.setContent(value);
        const { imgMaxWidth } = this.props;
        if (imgMaxWidth) {
            editor.on('nodechange', (e: { element: Element, parents: Node[], selectionChange?: boolean }) => {
                if (e && e.element.nodeName.toLowerCase() == 'img') {
                    // @ts-ignore
                    let width = e.element.width;
                    // @ts-ignore
                    let height = e.element.height;
                    if (width > imgMaxWidth) {
                        height = height / (width / imgMaxWidth);
                        width = imgMaxWidth;
                    }
                    this.editor.dom.setAttribs(e.element, { width, height });
                }
            });
        }

        if (Utils.isFunction(this.props.onEditorChange)) {
            // 兼容react外面点击判断问题
            editor.on('blur', (e: any) => {
                this.props.onFloating(true);
            });
            editor.on('focus', (e: any) => {
                this.props.onFloating(false);
            });
            editor.on('change keyup setcontent', (e: any) => {
                this.currentContent = editor.getContent();
                if (Utils.isFunction(this.props.onEditorChange)) {
                    this.props.onEditorChange(this.currentContent);
                }
            });
        }
        // 绑定事件
        Utils.bindHandlers(this.props, editor, initEvent);
    }

    /**
     * 行内富文本模式
     * @returns {(HTMLAnchorElement | HTMLAppletElement | HTMLAreaElement | HTMLAudioElement | HTMLBaseElement | HTMLBaseFontElement | HTMLQuoteElement | HTMLBodyElement | HTMLBRElement | HTMLButtonElement | HTMLCanvasElement | HTMLTableCaptionElement | HTMLTableColElement | HTMLDataElement | HTMLDataListElement | HTMLModElement | HTMLDirectoryElement | HTMLDivElement | HTMLDListElement | HTMLEmbedElement | HTMLFieldSetElement | HTMLFontElement | HTMLFormElement | HTMLFrameElement | HTMLFrameSetElement | HTMLHeadingElement | HTMLHeadElement | HTMLHRElement | HTMLHtmlElement | HTMLIFrameElement | HTMLImageElement | HTMLInputElement | HTMLUnknownElement | HTMLLabelElement | HTMLLegendElement | HTMLLIElement | HTMLLinkElement | HTMLPreElement | HTMLMapElement | HTMLMarqueeElement | HTMLMenuElement | HTMLMetaElement | HTMLMeterElement | HTMLObjectElement | HTMLOListElement | HTMLOptGroupElement | HTMLOptionElement | HTMLOutputElement | HTMLParagraphElement | HTMLParamElement | HTMLPictureElement | HTMLProgressElement | HTMLScriptElement | HTMLSelectElement | HTMLSourceElement | HTMLSpanElement | HTMLStyleElement | HTMLTableElement | HTMLTableSectionElement | HTMLTableDataCellElement | HTMLTemplateElement | HTMLTextAreaElement | HTMLTableHeaderCellElement | HTMLTimeElement | HTMLTitleElement | HTMLTableRowElement | HTMLTrackElement | HTMLUListElement | HTMLVideoElement | MSHTMLWebViewElement) & HTMLElement}
     */
    private renderInline() {
        const { tagName = 'div' } = this.props;
        return React.createElement(tagName, {
            ref: (elm) => (this.element = elm),
            id: this.id,
        });
    }

    /**
     * 正常模式
     * @returns {any}
     */
    private renderIframe() {
        return <textarea ref={(elm) => (this.element = elm)} style={{ visibility: 'hidden' }} id={this.id} />;
    }
}
